import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramCleanroomUpstreamChallenge } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';

const KEY = 'cleanroom.calc.upstreamChallenge.v2';

type GeneratorType = 'nozzle' | 'jet';
type Fields = { genType: GeneratorType; count: string; cfm: string; concentration: string };

const EMPTY: Fields = { genType: 'jet', count: '', cfm: '', concentration: '' };
const FACTOR: Record<GeneratorType, number> = { nozzle: 13500, jet: 3375 };

export default function CleanroomUpstreamChallengeScreen() {
  const [fields, setFields, { clear }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<'count' | 'cfm' | 'concentration' | null>(null);

  const set = useCallback((patch: Partial<Fields>) => setFields((p) => ({ ...p, ...patch })), [setFields]);

  const factor = FACTOR[fields.genType];
  const genLabel = fields.genType === 'nozzle' ? 'Nozzles' : 'Jets';

  const calculate = useCallback(() => {
    setError(null);
    const { count, cfm, concentration } = fields;
    const filled = [count, cfm, concentration].filter((v) => v.trim() !== '').length;
    if (filled < 2) { setError('Fill any 2 of the 3 fields to solve for the missing one.'); return; }
    if (filled === 3) { setError('All 3 values are filled — clear one to solve for it.'); return; }

    const n = parseFloat(count);
    const q = parseFloat(cfm);
    const c = parseFloat(concentration);

    if (concentration.trim() === '') {
      if (!Number.isFinite(n) || n <= 0) { setError(`# of ${genLabel} must be positive.`); return; }
      if (!Number.isFinite(q) || q <= 0) { setError('Airflow must be positive.'); return; }
      setSolvedFor('concentration');
      set({ concentration: ((n * factor) / q).toFixed(1) });
    } else if (count.trim() === '') {
      if (!Number.isFinite(c) || c <= 0) { setError('Concentration must be positive.'); return; }
      if (!Number.isFinite(q) || q <= 0) { setError('Airflow must be positive.'); return; }
      setSolvedFor('count');
      set({ count: ((c * q) / factor).toFixed(2) });
    } else {
      if (!Number.isFinite(n) || n <= 0) { setError(`# of ${genLabel} must be positive.`); return; }
      if (!Number.isFinite(c) || c <= 0) { setError('Concentration must be positive.'); return; }
      setSolvedFor('cfm');
      set({ cfm: ((n * factor) / c).toFixed(1) });
    }
  }, [fields, factor, genLabel, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clear();
  }, [clear]);

  const result = useMemo(() => [
    fields.count        && { label: `# of ${genLabel}`, value: fields.count,        emphasis: solvedFor === 'count' },
    fields.cfm          && { label: 'Airflow',           value: fields.cfm,          unit: 'CFM',  emphasis: solvedFor === 'cfm' },
    fields.concentration && { label: 'Concentration',    value: fields.concentration, unit: 'μg/L', emphasis: solvedFor === 'concentration' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[], [fields, genLabel, solvedFor]);

  return (
    <CalcShell
      title="Upstream Challenge"
      subtitle="Cleanroom · HEPA/ULPA Integrity Testing"
      formula={
        <>
          <FormulaLine>Nozzle: (# Nozzles × 13,500) / CFM = μg/L</FormulaLine>
          <FormulaLine>Jet:    (# Jets × 3,375) / CFM = μg/L</FormulaLine>
          <FormulaNote>
            C = upstream challenge concentration (μg/L){'\n'}
            N = number of generators · Q = airflow (CFM)
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: C = (N × factor) / CFM"
      diagram={<DiagramCleanroomUpstreamChallenge />}
    >
      <SegmentedButtons
        value={fields.genType}
        onValueChange={(v) => { setSolvedFor(null); set({ genType: v as GeneratorType }); }}
        buttons={[
          { value: 'nozzle', label: 'Nozzle (×13,500)' },
          { value: 'jet',    label: 'Jet (×3,375)' },
        ]}
        style={styles.segmented}
      />

      <View style={styles.field}>
        <NumericField
          label={`# of ${genLabel}`}
          value={fields.count}
          onChangeText={(v) => { setSolvedFor(null); set({ count: v }); }}
          maxDecimals={0}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={styles.field}>
        <NumericField
          label="Airflow"
          value={fields.cfm}
          onChangeText={(v) => { setSolvedFor(null); set({ cfm: v }); }}
          rightText="CFM"
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={styles.field}>
        <NumericField
          label="Concentration"
          value={fields.concentration}
          onChangeText={(v) => { setSolvedFor(null); set({ concentration: v }); }}
          rightText="μg/L"
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
}

const styles = StyleSheet.create({
  segmented: { marginBottom: 16 },
  field: { marginBottom: 12 },
});
